import React, { useState, useMemo } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Grid, Paper, useTheme, alpha, Button, Typography } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import WorkflowV2Stepper from '../../components/workflow-v2/WorkflowV2Stepper';
import { useWorkflowContext } from '../../context/WorkflowContext';

const steps = [
  { 
    label: 'File Upload', 
    path: '',
    description: 'Getting Started: Upload your keyword data (CSV files only) and tell us about your business to begin the workflow.'
  },
  { 
    label: 'Query Analysis', 
    path: 'keyword',
    description: 'Smart Analysis: AI analyzes your keywords to identify and organize topics and subtopics for better content structure and categorization.'
  },
  { 
    label: 'Intent Analysis', 
    path: 'semantic',
    description: 'Smart Analysis: AI creates smart categories and classifies each keyword based on semantic meaning and search intent.'
  },
  { 
    label: 'Batch Configuration', 
    path: 'batch_config',
    description: 'Smart Analysis: Review and configure the topics and categories identified from previous analysis steps before batch processing.'
  },
  { 
    label: 'Batch Processing', 
    path: 'batch_run',
    description: 'Smart Analysis: Execute batch run to classify each keyword into the configured categories. This process may take some time depending on the number of keywords.'
  },
  { 
    label: 'SERP API Tool', 
    path: 'serp_tool',
    description: 'Data Enrichment: Call SERP API to enrich sample keywords with search result data, rankings, and competitive insights.'
  },
  { 
    label: 'DataForSEO Volume Analysis', 
    path: 'data_for_seo',
    description: 'Data Enrichment: Analyze sample keywords using DataForSEO to gather advanced SEO metrics, search volume, and enrichment data.'
  },
  { 
    label: 'Workflow Summary', 
    path: 'summary',
    description: 'Overview: View workflow progress, step statuses, and quick access to all workflow components.'
  },
  { 
    label: 'Explore Results', 
    path: 'explore',
    description: 'Your Results: Explore interactive dashboards, chat with AI Assistant to get valuable insights, and discover actionable opportunities from your enriched data.'
  }
];

const WorkflowV2Layout: React.FC = () => {
  const { clientId: paramClientId, workflowId: paramWorkflowId } = useParams<{ clientId: string; workflowId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [stepperCollapsed, setStepperCollapsed] = useState(true);
  
  // Get client and workflow data from context (prioritize context over URL params)
  const contextData = useWorkflowContext();
  const clientId = contextData.clientId || paramClientId;
  const workflowId = contextData.workflowId || paramWorkflowId;
  const clientName = contextData.clientName || `Client ${clientId}`;
  const workflowName = contextData.workflowName || contextData.workflow?.name || `Workflow ${workflowId}`;

  // Determine current step based on URL path
  const { currentStepTitle, currentStepNumber } = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    
    const stepIndex = steps.findIndex(step => {
      if (step.path === '') {
        return lastPart === workflowId;
      }
      return lastPart === step.path;
    });
    
    const index = stepIndex >= 0 ? stepIndex : 0;
    return {
      currentStepTitle: steps[index].label,
      currentStepNumber: index + 1
    };
  }, [location.pathname, workflowId]);
  
  return (
    <Box sx={{ py: 3, px: 2 }}>
      <Grid container spacing={3} columnGap={!stepperCollapsed ? 3 : 0}>
        {/* Left Side: Stepper */}
        <Grid item xs="auto">
          {clientId && workflowId && (
            <WorkflowV2Stepper
              clientId={clientId}
              workflowId={workflowId}
              onCollapseChange={setStepperCollapsed}
              initialCollapsed={true}
            />
          )}
        </Grid>
        
        {/* Right Side: Content */}
        <Grid 
          item 
          xs
          sx={{
            pl: stepperCollapsed ? 1 : 3,
            position: 'relative'
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h5" color="text.primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                Step {currentStepNumber}: {currentStepTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" display="block">
                Client: {clientId} - {clientName} • Workflow: {workflowId} - {workflowName}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/start-process')}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none'
              }}
            >
              Back to Client List
            </Button>
          </Box>
          
          <Paper
            elevation={2}
            sx={{
              p: 4,
              minHeight: 'calc(100vh - 180px)',
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
            }}
          >
            <Outlet />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkflowV2Layout;

